import { PageNotice } from '../../../shared/components/PageFeedback';
import PartyActivityMetrics from '../../party-activity/PartyActivityMetrics';
import PartyActivityMetricDialog from '../../party-activity/PartyActivityMetricDialog';
import usePartyActivity from '../../party-activity/usePartyActivity';
import useRegisteredModuleFilters from '../../moduleFilters';
import { useState } from 'react';
import SupplierCard from '../components/SupplierCard.jsx';
import SupplierDialog from '../components/SupplierDialog.jsx';
import SupplierDetailDialog from '../components/SupplierDetailDialog.jsx';
import { useSuppliersData } from '@/modules/suppliers/hooks/useSuppliersData';
import {
  AdaptiveFilterBar,
  EntityCardGrid,
  ConfirmActionDialog,
  PageActionRow,
  PageDivider,
  PageFrame,
  PageHeader,
  PrimaryActionButton,
} from '../../../shared/components/patterns';
import { toast } from '../../../shared/hooks/use-toast';
import '@/modules/suppliers/styles/suppliers.css';

const suppliersFilterConfig = {
  search: {
    key: 'query',
    placeholder: 'Cerca fornitore o azienda...',
  },
  groups: [
    {
      key: 'status',
      label: 'Stato',
      selection: 'single',
      emptyValue: 'ALL',
      options: [
        { value: 'ACTIVE', label: 'Attivi' },
        { value: 'INACTIVE', label: 'Inattivi' },
      ],
    },
  ],
};

export default function SuppliersPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('ALL');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [statusRequest, setStatusRequest] = useState(null);
  const [deleteRequest, setDeleteRequest] = useState(null);
  const {
    filteredSuppliers: baseSuppliers,
    suppliers = baseSuppliers,
    catalogStatus,
    isLoading,
    loadError,
    createSupplier,
    updateSupplier,
    archiveSupplier,
    restoreSupplier,
    deleteSupplierPermanently,
  } = useSuppliersData({ search, status });
  const moduleFilters = useRegisteredModuleFilters('suppliers', suppliers);
  const activity = usePartyActivity('supplier', suppliers, {
    enabled: ['available', 'empty'].includes(catalogStatus), loading: isLoading,
    onMetricClick: () => { setSearch(''); setStatus('ALL'); moduleFilters.onReset(); },
  });
  const filteredSuppliers = activity.filterRows(moduleFilters.filterRows(baseSuppliers));
  const activeSuppliers = filteredSuppliers.filter((supplier) => supplier.status === 'ACTIVE');
  const archivedSuppliers = filteredSuppliers.filter((supplier) => supplier.status !== 'ACTIVE');

  const moduleBlocked = catalogStatus === 'module_disabled' || catalogStatus === 'tenant_database_unavailable';

  const openCreate = () => {
    setEditingSupplier(null);
    setDialogOpen(true);
  };

  const openEdit = (supplier) => {
    setEditingSupplier(supplier);
    setDialogOpen(true);
  };

  const saveSupplier = (payload) => (payload.id ? updateSupplier(payload) : createSupplier(payload));

  const toggleSupplierStatus = async (supplier) => {
    setStatusRequest(supplier);
  };

  const confirmSupplierStatus = async () => {
    const supplier = statusRequest;
    const active = supplier.status === 'ACTIVE';
    const result = active
      ? await archiveSupplier(supplier.id)
      : await restoreSupplier(supplier.id);
    setStatusRequest(null);
    if (!result.ok) {
      toast({
        title: 'Fornitore non aggiornato',
        description: result.message || 'Impossibile aggiornare lo stato del fornitore.',
        variant: 'destructive',
      });
      return;
    }
    setSelectedSupplier(current => current?.id === supplier.id ? { ...current, status: active ? 'INACTIVE' : 'ACTIVE' } : current);
    toast({ title: 'Fornitore aggiornato', description: result.message || 'Stato fornitore aggiornato.' });
  };

  return (
    <PageFrame filterModel={moduleFilters} className="suppliers-page">
      <PageHeader
        title="Fornitori"
        description="Gestisci i tuoi fornitori e le relazioni commerciali"
      />

      {loadError ? <PageNotice role="alert" className="suppliers-alert wt-inline-error">{loadError}</PageNotice> : null}

      {!moduleBlocked ? (
        <>
          <PartyActivityMetrics activity={activity} />
          <PartyActivityMetricDialog kind="supplier" activity={activity} onSelect={setSelectedSupplier} />
          <PageDivider />
          <AdaptiveFilterBar
            config={{ ...suppliersFilterConfig, groups: [...suppliersFilterConfig.groups, activity.filterGroup] }}
            values={{ query: search, status, activity: activity.overview }}
            onChange={(key, value) => {
              if (key === 'query') setSearch(value);
              if (key === 'status') setStatus(value || 'ALL');
              if (key === 'activity') activity.setFilter(value);
            }}
            onReset={() => {
              setSearch('');
              setStatus('ALL');
              activity.reset();
            }}
          />
          <PageDivider />
          <PageActionRow>
            <PrimaryActionButton onClick={openCreate}>Nuovo</PrimaryActionButton>
          </PageActionRow>
        </>
      ) : null}

      {!moduleBlocked && isLoading ? <div className="suppliers-empty">Caricamento fornitori...</div> : null}
      {!moduleBlocked && !isLoading && filteredSuppliers.length === 0 ? (
        <div className="suppliers-empty">Nessun fornitore trovato.</div>
      ) : null}

      {!moduleBlocked && filteredSuppliers.length > 0 ? (
        <div className="suppliers-list-sections">
          {[
            ['Attivi', activeSuppliers],
            ['Disattivati', archivedSuppliers],
          ].map(([title, items]) => (
            items.length > 0 ? (
              <section className="suppliers-list-section" key={title}>
                <div className="suppliers-list-section-header">
                  <h2>{title}</h2>
                  <span>{items.length}</span>
                </div>
                <EntityCardGrid>
                  {items.map((supplier) => (
                    <SupplierCard
                      key={supplier.id}
                      supplier={supplier}
                      onDetail={setSelectedSupplier}
                      onEdit={openEdit}
                      onArchive={toggleSupplierStatus}
                      onDelete={setDeleteRequest}
                    />
                  ))}
                </EntityCardGrid>
              </section>
            ) : null
          ))}
        </div>
      ) : null}

      {!moduleBlocked ? (
        <SupplierDialog
          open={dialogOpen}
          supplier={editingSupplier}
          onClose={() => setDialogOpen(false)}
          onSave={saveSupplier}
        />
      ) : null}

      {!moduleBlocked ? (
        <SupplierDetailDialog
          open={Boolean(selectedSupplier)}
          supplier={selectedSupplier}
          onClose={() => setSelectedSupplier(null)}
          onSave={async (supplier) => { const result = await saveSupplier(supplier); if (result?.ok) setSelectedSupplier(result.data?.supplier || supplier); return result; }}
          onArchive={toggleSupplierStatus}
          onDelete={setDeleteRequest}
        />
      ) : null}
      <ConfirmActionDialog
        open={Boolean(statusRequest)}
        title={statusRequest?.status === 'ACTIVE' ? 'Disattivare il fornitore?' : 'Riattivare il fornitore?'}
        description={`Confermi l'aggiornamento del fornitore "${statusRequest?.displayName || statusRequest?.legalName || ''}"?`}
        confirmLabel={statusRequest?.status === 'ACTIVE' ? 'Disattiva' : 'Riattiva'}
        cancelLabel="Annulla"
        tone={statusRequest?.status === 'ACTIVE' ? 'danger' : 'default'}
        onCancel={() => setStatusRequest(null)}
        onConfirm={confirmSupplierStatus}
      />
      <ConfirmActionDialog
        open={Boolean(deleteRequest)}
        title="Eliminare definitivamente il fornitore?"
        description={`Il fornitore "${deleteRequest?.displayName || deleteRequest?.legalName || ''}" verrà eliminato in modo irreversibile.`}
        confirmLabel="Elimina definitivamente"
        cancelLabel="Annulla"
        tone="danger"
        onCancel={() => setDeleteRequest(null)}
        onConfirm={async () => {
          const result = await deleteSupplierPermanently(deleteRequest.id);
          if (result.ok) {
            setSelectedSupplier(current => current?.id === deleteRequest.id ? null : current);
            setDeleteRequest(null);
          }
          else toast({ title: 'Fornitore non eliminato', description: result.message || 'Esistono relazioni collegate.', variant: 'destructive' });
        }}
      />
    </PageFrame>
  );
}
